import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { isConnected, connectOpenAI, askTutor } from '../services/openai';
import { addChat } from '../data/db';
import { useAuth } from '../services/AuthContext';
import {
  ButtonPrimary,
  ButtonSecondary,
  Card,
  Input,
  Badge,
  Spinner,
  Icon
} from '../components/ComponentLibrary';
import { Send, Bot, User, Sparkles, MessageSquare, Plus, History, X, Menu } from 'lucide-react';
import './AINew.css';

const LOCAL_STORAGE_HISTORY_KEY = 'codewavee_ai_historyv2';

export default function AI() {
  const { user } = useAuth();
  const [connected, setConnected] = useState(isConnected());
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const logRef = useRef();

  // Load history on mount
  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_HISTORY_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConversations(parsed);
        if (parsed.length > 0) {
          setActiveId(parsed[0].id);
        } else {
          startNewChat();
        }
      } catch (e) {
        console.error("Failed to parse history", e);
        startNewChat();
      }
    } else {
      startNewChat();
    }
  }, []);

  // Save history whenever it changes
  useEffect(() => {
    if (conversations.length > 0) {
      localStorage.setItem(LOCAL_STORAGE_HISTORY_KEY, JSON.stringify(conversations));
    }
  }, [conversations]);

  // Scroll to bottom
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [conversations, activeId, isTyping]);

  const activeChat = conversations.find(c => c.id === activeId) || { messages: [] };
  const messages = activeChat.messages;

  function startNewChat() {
    const newId = Date.now().toString();
    const welcomeMessages = [
      `مرحباً! 👋 أنا مساعدك الذكي، موجود هنا لمساعدتك في أي شيء تريد تعلمه في البرمجة! 🚀`,
      `سعيد بلقائك! 😊 اسألني أي سؤال عن بايثون أو سكراتش أو تطوير الويب! 💡`,
      `أهلاً بك! 🌟 أنا هنا لأجعل التعلم ممتعاً ومفيداً لك! 📚`,
    ];
    const randomWelcome = welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];

    const newChat = {
      id: newId,
      title: 'محادثة جديدة',
      messages: [{
        id: 'welcome-' + newId,
        role: 'bot',
        text: randomWelcome,
        ts: new Date().toISOString()
      }],
      lastUpdated: new Date().toISOString()
    };

    setConversations(prev => [newChat, ...prev]);
    setActiveId(newId);
    setShowHistory(false);
  }

  async function onConnect() {
    const res = await connectOpenAI();
    if (res.ok) {
      setConnected(true);
    } else {
      alert("❌ خطأ في الاتصال:\n" + res.message);
    }
  }

  async function send(msgText = text) {
    if (!msgText.trim()) return;

    const userMsg = {
      id: Date.now() + '-u',
      role: 'user',
      text: msgText,
      ts: new Date().toISOString()
    };

    // Update locally
    setConversations(prev => prev.map(c => {
      if (c.id === activeId) {
        const updatedMessages = [...c.messages, userMsg];
        // Set title from first user message if it's default
        let title = c.title;
        if (title === 'محادثة جديدة') {
          title = msgText.length > 20 ? msgText.substring(0, 20) + '...' : msgText;
        }
        return { ...c, messages: updatedMessages, title, lastUpdated: new Date().toISOString() };
      }
      return c;
    }));

    setText('');
    setIsTyping(true);

    try {
      const bot = await askTutor(userMsg.text);

      const suggestionMatch = bot.text.match(/\[SUGGESTION\](.*?)\[\/SUGGESTION\]/s);
      let suggestions = [];
      let displayText = bot.text;

      if (suggestionMatch) {
        const rawSuggestions = suggestionMatch[1].trim();
        suggestions = rawSuggestions.split('|').map(s => s.trim()).filter(s => s);
        displayText = bot.text.replace(suggestionMatch[0], '').trim();
      }

      const botMsg = {
        id: bot.id + '-b',
        role: 'bot',
        text: displayText,
        suggestions,
        ts: bot.timestamp
      };

      setConversations(prev => prev.map(c => {
        if (c.id === activeId) {
          return { ...c, messages: [...c.messages, botMsg], lastUpdated: new Date().toISOString() };
        }
        return c;
      }));

      // Also record to main DB
      addChat({
        userId: user?.id || 'child-1',
        question: userMsg.text,
        answer: bot.text,
        ts: new Date().toISOString()
      });
    } catch (error) {
      console.error('Chat error:', error);
    } finally {
      setIsTyping(false);
    }
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  function deleteChat(e, id) {
    e.stopPropagation();
    const updated = conversations.filter(c => c.id !== id);
    setConversations(updated);
    if (activeId === id) {
      if (updated.length > 0) setActiveId(updated[0].id);
      else startNewChat();
    }
  }

  return (
    <div className="ai-page">
      {/* Mobile Sidebar Backdrop */}
      {showHistory && (
        <div className="sidebar-backdrop" onClick={() => setShowHistory(false)}></div>
      )}

      {/* Sidebar / History Drawer */}
      <aside className={`ai-sidebar ${showHistory ? 'show' : ''}`}>
        <div className="sidebar-header">
          <History size={20} />
          <span>تاريخ المحادثات</span>
          <button className="close-sidebar-btn" onClick={() => setShowHistory(false)}>
            <X size={20} />
          </button>
        </div>

        <button className="new-chat-btn" onClick={startNewChat}>
          <Plus size={18} /> محادثة جديدة
        </button>

        <div className="history-list">
          {conversations.map(c => (
            <div
              key={c.id}
              className={`history-item ${c.id === activeId ? 'active' : ''}`}
              onClick={() => { setActiveId(c.id); setShowHistory(false); }}
            >
              <MessageSquare size={16} />
              <span className="history-title">{c.title}</span>
              <button className="delete-chat-btn" onClick={(e) => deleteChat(e, c.id)}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </aside>

      <div className="ai-main">
        <header className="ai-header">
          <div className="header-left">
            <button className="menu-toggle-btn" onClick={() => setShowHistory(true)}>
              <Menu size={24} />
            </button>
          </div>
          <div className="ai-title-wrapper">
            <h1 className="ai-title">المساعد الذكي</h1>
            <Badge variant="ocean" size="sm" className="ai-badge-desktop">مدعوم بالذكاء الاصطناعي</Badge>
          </div>
          <div className="header-right">
            <button className="mobile-new-chat-btn" onClick={startNewChat} title="محادثة جديدة">
              <Plus size={24} />
            </button>
            <div className="bot-status-compact desktop-only">
              <span className={`status-dot ${connected ? 'online' : 'offline'}`}></span>
              <span>{connected ? 'متصل' : 'غير متصل'}</span>
            </div>
          </div>
        </header>

        <section className="chat-layout">
          <div className="chat-messages" ref={logRef}>
            {messages.map(m => (
              <div key={m.id} className={`message-wrapper ${m.role === 'user' ? 'user-wrapper' : 'bot-wrapper'}`}>
                <div className="message-avatar">
                  {m.role === 'user' ? <User size={20} /> : <Bot size={20} />}
                </div>
                <div className="message-content">
                  <div className="message-bubble">
                    <div className="markdown-content">
                      <ReactMarkdown>{m.text}</ReactMarkdown>
                    </div>
                    {m.suggestions && m.suggestions.length > 0 && (
                      <div className="suggestions-list">
                        {m.suggestions.map((s, idx) => (
                          <button key={idx} className="suggestion-item" onClick={() => send(s)}>
                            <Sparkles size={12} /> {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="message-time">
                    {new Date(m.ts).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="message-wrapper bot-wrapper">
                <div className="message-avatar">
                  <Bot size={20} />
                </div>
                <div className="message-content">
                  <div className="message-bubble typing-bubble">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="chat-input-area">
            {!connected && (
              <div className="connection-overlay">
                <ButtonPrimary onClick={onConnect} size="sm">تفعيل المساعد الذكي</ButtonPrimary>
              </div>
            )}
            <div className="input-box-container">
              <textarea
                value={text}
                onChange={e => {
                  setText(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                onKeyDown={handleKeyPress}
                placeholder="اكتب سؤالك هنا..."
                rows="1"
                className="chat-textarea"
                style={{ height: 'auto', maxHeight: '120px' }}
              />
              <button
                className={`send-action-btn ${!text.trim() || isTyping ? 'disabled' : ''}`}
                onClick={() => send()}
                disabled={!text.trim() || isTyping}
              >
                {isTyping ? <Spinner size="sm" /> : <Send size={20} />}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
